/* The shape of everything the portal renders.
   ------------------------------------------------------------------------
   This file is a CONTRACT. Several sessions build against it at once (see
   PLAN.md); add to it, do not reshape what is already here.
   ------------------------------------------------------------------------ */

/* A piece of text in the material. Czech is primary: `cs` is required and `en`
   is not, because 59 examples were written for Czech curricula and translating
   them is months of work, not an afternoon. A missing `en` renders the Czech
   with a quiet "untranslated" mark — never an empty page.

   A bare string is shorthand for { cs }. Writing new Czech material should not
   cost a wrapper object on every field. */
export type Localized = string | { cs: string; en?: string };

export type Locale = "cs" | "en";

/* ---------- blocks ----------

   The block model is carried over from the static portal unchanged. It was
   worked out over two workshops and there is no reason to redesign it; what is
   new is that TypeScript now checks it. Adding a kind means a member here and a
   component in src/blocks/. */

export interface BlockBase {
  k: string;
  title?: Localized;
  lead?: Localized;
  note?: Localized[];
  noteTitle?: Localized;
  /* deck mode only: hold this block back until the n-th reveal */
  step?: number;
}

export interface TextBlock     extends BlockBase { k: "text"; body: Localized }
export interface HeadingBlock  extends BlockBase { k: "h" }
export interface RuleBlock     extends BlockBase { k: "rule" }
/* Where the sheet is cut. A dashed line and a pair of scissors, placed by
   whoever wrote the material at the point the paper is actually cut — between
   the cards, above the strip the pupil takes away. It is a block rather than a
   flag on the sheet because only the material knows where the cut falls, and a
   line drawn anywhere else is a decoration. `title` overrides the default
   label; most of the time it should not. */
export interface CutBlock      extends BlockBase { k: "cut" }
export interface PointsBlock   extends BlockBase { k: "points"; points: Localized[] }
export interface StepsBlock    extends BlockBase { k: "steps"; steps: { t: Localized; d?: Localized; s?: number }[]; prompt?: Localized }
export interface PromptBlock   extends BlockBase { k: "prompt"; prompt?: Localized; out?: Localized; promptLabel?: Localized; outLabel?: Localized }
export interface ExampleBlock  extends BlockBase { k: "example"; task?: Localized; input?: Localized; prompt?: Localized; out?: Localized }
export interface CompareBlock  extends BlockBase { k: "compare"; bad: CompareSide; good: CompareSide }
export interface TableBlock    extends BlockBase { k: "table"; head: Localized[]; rows: Localized[][] }
export interface CalloutBlock  extends BlockBase { k: "callout"; body?: Localized; points?: Localized[]; red?: boolean }
export interface ExerciseBlock extends BlockBase { k: "exercise"; minutes?: number; brief?: Localized; prompt?: Localized; done?: Localized[] }
export interface CardsBlock    extends BlockBase { k: "cards"; cols?: 2 | 3; items: CardItem[] }
export interface QuotesBlock   extends BlockBase { k: "quotes"; items: Localized[] }
export interface SigBlock      extends BlockBase { k: "sig"; lines: Localized[] }
export interface FigureBlock   extends BlockBase { k: "figure"; fig: string; caption?: Localized; opts?: Record<string, unknown> }
export interface AskBlock      extends BlockBase { k: "ask"; q: Localized; out?: Localized; outLabel?: Localized; answer?: Localized; body?: Localized; astep?: number }

/* The one interactive block in the material. A chat hands the application back
   as text, and turning text into a file is where a room without paid tools
   stops; this makes the file in the page. `gen: true` adds the generator above
   it — paste the filled-in brief, get the finished application back — which is
   the half that needs a sign-in, because it spends money. Both halves are
   switchable on the server. */
export interface MakerBlock    extends BlockBase { k: "maker"; gen?: boolean; genLabel?: Localized; genHint?: Localized; fileName?: string }

/* A running application inside a page, with the prompt that makes another one
   like it. A lesson reaches its application through the kit; a general page has
   only blocks, and a page that describes an interactive element without running
   one is the thing this material exists to argue against. `app` names a file in
   content/apps/ — the same files, served the same way, checked by the same
   gate. */
/* The screen an application is shown on: a Mac display or a laptop for what
   runs at the projector, an iPhone for what a student would hold.

   **Every running application is shown on one of them.** A teacher decides
   whether to make a material by looking at it, and what is being decided about
   is not a panel of this portal — it is a file that will be opened on the
   machine wired to the projector, or handed to a class on the school laptops. A
   bare rectangle in the middle of a page says the opposite, and the reader has
   to be told in prose what the picture should have said. So the field is
   optional and its absence means `mac`, the screen at the front of the room;
   content names a device only to say something other than the default. The
   counterpart rule is on paper — see `.paper` in catalog.css. */
export type DeviceKind = "mac" | "macbook" | "iphone";

export interface AppBlock      extends BlockBase { k: "app"; app: string; height?: number; device?: DeviceKind; prompt?: PromptSet }

export interface CompareSide { label?: Localized; text?: Localized; out?: Localized }
export interface CardItem { n?: Localized; title: Localized; d?: Localized; foot?: Localized; href?: string }

export type Block =
  | TextBlock | HeadingBlock | RuleBlock | CutBlock | PointsBlock | StepsBlock | PromptBlock
  | ExampleBlock | CompareBlock | TableBlock | CalloutBlock | ExerciseBlock
  | CardsBlock | QuotesBlock | SigBlock | FigureBlock | AskBlock
  | MakerBlock | AppBlock;

export type BlockKind = Block["k"];

/* ---------- the lesson ----------

   The head of a lesson, and the reason the portal was turned inside out. It
   used to teach a method for arriving at a goal; teachers already have one. It
   now states the goal and hands over the finished materials that reach it.

   The enumerations below are the contract, and they are the contract in three
   places at once: here, in content/schema/doc.schema.json for the editor, and
   in scripts/check-portal.mjs, which enforces the semantics. The gate reads all
   three and fails when they disagree, so add a kind in one place and the push
   is refused until it is added in the other two. Keep the literal `as const`
   arrays on one recognisable line each; the gate parses them. */

/* One goal, three levels of reaching it. Never three different topics.

   The labels a reader sees are Základní / Očekávaná / Rozšířená. Which pupil a
   level is for is said in `who`, which the teacher reads — the wall a class can
   see never carries the words "weak pupil". */
export const GOAL_LEVELS = ["zaklad", "bezny", "rozsireny"] as const;
export type GoalLevelId = (typeof GOAL_LEVELS)[number];

export interface GoalLevel {
  id: GoalLevelId;
  /* Which pupil this level is for. Read by the teacher, not projected. */
  who: Localized;
  /* What the pupil does. A verb that can be watched happening: "zapíše",
     "sestaví", "vysvětlí". Not "chápe" — nobody has ever seen chápání. */
  can: Localized;
  /* The measure. How many out of how many, in what time, with what at hand.
     Without it the goal is a wish, and the gate says so. */
  proof: Localized;
}

export interface Goal {
  lead?: Localized;
  /* Exactly three, in the order of GOAL_LEVELS. */
  levels: GoalLevel[];
}

/* The ten materials of a lesson. A fixed set: a lesson either has one of each
   or says why it does not. The order here is the order they are shown in. */
export const KIT_KINDS = ["vyklad", "hands-on", "samostatna", "tymova", "overeni", "samostudium", "aplikace", "audio-vyklad", "audio-shrnuti", "predani"] as const;
export type KitKind = (typeof KIT_KINDS)[number];

/* How a finished material is carried. Print is not among them: a printable
   sheet is derived from `blocks`, which is what `print` on the item says. */
export const READY_KINDS = ["blocks", "app", "audio"] as const;
export type ReadyKind = (typeof READY_KINDS)[number];

/* Prose, tables, prompts — the material itself, rendered where the reader
   opens it and printed when `print` is set. */
export interface ReadyBlocks { as: "blocks"; blocks: Block[] }

/* One self-contained HTML file in content/apps/. It runs in a sandboxed frame
   and can be downloaded, because a teacher tries it the way the class will use
   it: as a file, outside the portal. */
export interface ReadyApp { as: "app"; app: string; height?: number; device?: DeviceKind }

/* A recording, and the script it was read from.

   The recording lives in the database, never in git: it is binary, it would
   grow both images by tens of megabytes and it cannot be reviewed in a diff.
   The script is content and is always here — it is read, printed, corrected
   and recorded again, and it is the half the gate can check. A missing
   recording is not a broken page; the script simply stands on its own. */
export interface ReadyAudio { as: "audio"; audio?: string; script: Localized; minutes?: number }

export type Ready = ReadyBlocks | ReadyApp | ReadyAudio;

/* One prompt, three tabs.

   `core` is the whole prompt. A per-model field holds only what that model
   needs said differently — length, output format, how one asks it for a file —
   and is appended to the core under a blank line. The reader copies a finished
   text and never assembles anything.

   Formatting follows the destination, as everywhere else: a prompt pasted into
   a chat is plain text, and a prompt saved as an instruction file carries
   markdown and names the file in `label`. */
export const PROMPT_MODELS = ["claude", "gpt", "gemini"] as const;
export type PromptModel = (typeof PROMPT_MODELS)[number];

export interface PromptSet {
  core: Localized;
  claude?: Localized;
  gpt?: Localized;
  gemini?: Localized;
  /* The file name, when the prompt is saved rather than pasted. */
  label?: string;
}

interface KitItemBase {
  kind: KitKind;
  note?: Localized[];
}

export interface KitItemPresent extends KitItemBase {
  title: Localized;
  /* The one line shown while the item is collapsed. */
  claim: Localized;
  minutes?: number;
  /* Which levels of the goal this material serves. The promise that every
     pupil gets somewhere is only checkable because this field exists: the gate
     refuses a lesson whose kit leaves a level unserved. */
  levels: GoalLevelId[];
  /* The finished material. Optional, and that is the whole of the third state
     a kit item can be in: not made yet, but the prompt that makes it is here.
     Item 12 of PLAN.md converts fifty-four examples into lessons that are in
     exactly that state — a goal and ten prompts — and the finished materials
     arrive one at a time afterwards. A row without it says so; it does not
     pretend. What must never happen is the reverse, a row that looks finished
     and is not, which is why the mark is drawn from this field and from
     nothing else. */
  ready?: Ready;
  prompt: PromptSet;
  /* Meant for paper. Only where the material is blocks — there is nothing to
     print about a recording. */
  print?: boolean;
  absent?: undefined;
}

/* A material that is not here, and why. Said out loud rather than left out:
   ten rows always, so a reader can see what a lesson does not have. */
export interface KitItemAbsent extends KitItemBase {
  absent: Localized;
}

export type KitItem = KitItemPresent | KitItemAbsent;

export function isAbsent(item: KitItem): item is KitItemAbsent {
  return (item as KitItemAbsent).absent !== undefined;
}

/* Whether the finished material is actually here. Asked by the row's mark and
   by the count on the tile; a lesson is filled when this is true of every item
   that is not deliberately absent. */
export function isReady(item: KitItem): boolean {
  return !isAbsent(item) && (item as KitItemPresent).ready !== undefined;
}

/* ---------- documents ---------- */

export type DocType = "page" | "lesson";

export interface DocMeta {
  subject?: string;
  strand?: string[];
  year?: number;
  grade?: Localized;
  schoolType?: "zs" | "ss";
}

/* A named part of a document.

   Three pages of the material were never a flat list of blocks: the theory is
   36 slides, the planning method is five steps, and the prompt database is 30
   entries with tags. They look like three shapes and are one — a document made
   of named parts — so there is one field for all three rather than three
   special cases in the renderer.

   A part with a title folds; a part without one is simply content, which is
   how an introduction and a closing note get to sit either side of the parts
   they belong to. */
export interface Part {
  id?: string;
  title?: Localized;
  eyebrow?: Localized;
  lead?: Localized;
  /* The prompt database filters on these. */
  tags?: string[];
  /* Deck layout, read only in presentation mode — item 8 of docs/plan/00-prestavba-2026-08.md. */
  variant?: "title" | "big";
  blocks: Block[];
}

/* How the parts are presented. Explicit rather than inferred from their shape:
   a renderer that guesses is a renderer nobody can predict. */
export type Layout = "sections" | "slides" | "prompts";

interface DocBase {
  id: string;
  meta?: DocMeta;
  title: Localized;
  eyebrow?: Localized;
  claim?: Localized;
  lead?: Localized;
  layout?: Layout;
  /* Required on every document, a lesson included, where it is the run of
     prose above the goal — who this pupil is, what the situation is. A lesson
     with nothing to say there writes an empty list rather than leaving the
     field out: one shape for every document is worth an empty array. */
  blocks: Block[];
  parts?: Part[];
}

export interface PageDoc extends DocBase { type: "page" }

/* A lesson: a goal in three levels, and ten finished materials that reach it.
   `blocks` is the introduction; the body of the page is the kit. */
export interface LessonDoc extends DocBase {
  type: "lesson";
  goal: Goal;
  kit: KitItem[];
}

export type Doc = PageDoc | LessonDoc;

export function isLesson(doc: Doc): doc is LessonDoc {
  return doc.type === "lesson";
}

/* ---------- taxonomy ---------- */

export interface Strand {
  id: string;
  schoolType: "zs" | "ss";
  name: Localized;
  short: Localized;
  who: Localized;
  years: number[];
}

export interface Subject { id: string; name: Localized; area: string }

export interface Taxonomy {
  strandIds: string[];
  strands: Record<string, Strand>;
  subjects: Record<string, Subject>;
  areas: Record<string, Localized>;
}

/* One row of the catalogue index: everything a tile needs, without loading the
   document behind it. 59 documents is far too much to fetch to draw a grid. */
export interface IndexEntry {
  id: string;
  title: Localized;
  claim?: Localized;
  subject: string;
  strand: string[];
  year: number;
  /* The goal at the expected level, in one line. What a teacher scans a grid
     of tiles for; `claim` describes the material, this says where the pupil
     ends up. Lessons carry it, the old examples do not. */
  goal?: Localized;
  /* How many of the ten materials are finished. On the tile because a teacher
     choosing between two lessons has to see which of them is filled before
     opening it — a lesson that is a goal and ten prompts is worth having and
     is not the same offer as one with ten finished materials. Absent on the
     old examples, which have no kit to count.

     Not called `ready`: that name is taken by the carrier of a finished
     material, and two different things under one name in one file of types is
     the sort of detail somebody trips over a month later. */
  filled?: number;
}

/* ---------- server state ---------- */

/* Feature switches are server state, not a browser preference: switching the
   generator off has to switch it off for everyone, not for whoever clicked.

   `defaults` and `delegated` come from the server too. The administration page
   used to keep its own copy of the defaults, which went stale the moment a
   feature was added — the qr switch was missing from it — and the page had no
   way at all to know which switches a non-administrator may flip. */
export interface Settings {
  features: Record<string, boolean>;
  defaults?: Record<string, boolean>;
  delegated?: string[];
}

/* Three roles, and what each may do is the server's table — api/src/roles.ts.
   `can` travels with the account so the page can draw the tabs somebody
   actually has rather than offering four and refusing three. It decides
   nothing: every route asks the server again. */
export type Role = "admin" | "power" | "teacher";

export interface Me { id: string; username: string; role: Role; can: string[] }

export const can = (me: Me | null, capability: string): boolean =>
  me?.can?.includes(capability) === true;
